import React, { useMemo, useState, useCallback } from "react";
import Dashboard, { MenuContent } from "components/dashboard";
import { PageSection, PageHeaderBar } from "components/page";
import { useQuery, QueryResponseHandler } from "components/graphql";
import { FormattedMessage } from "react-intl";
import { startOfDay, endOfDay } from "date-fns";
import { EventListActionButtons } from "../event";
import EventPaginatedTable from "../event/table/table";

import { AlarmsPaginatedTable, getSourcesQuery } from "../source";
import { CustomerData } from "../customer/";
import { Restricted, RestrictedToScopedView, permissions, useAuthDataContext } from "components/auth";
import { useCurrentCustomerContext } from "components/current-customer-context";
import { getCustomerQuery } from "pages/customer";
import { parsePage } from "components/table";
import FormAdvancedField from "components/form/form-advanced-field";
import FormConfirm from "components/form/form-confirm";
import withStyles from "components/with-styles";
import styles from "./styles/home.css.js";
import Link from "components/link";
import { GENERAL_STATUS_KO } from "../../utils/constants";

const HomePage = ({ location: { search }, intl, classes }) => {
  const { currentCustomerId, customers } = useCurrentCustomerContext();

  const { hasAdminPrivileges } = useAuthDataContext();

  const [showAlarms, setShowAlarms] = useState(true);

  const query = useMemo(() => getCustomerQuery(currentCustomerId), [currentCustomerId]);
  const { data, loading, error } = useQuery(query);
  const customer = data.customer;

  const queryAlarms = useMemo(
    () =>
      getSourcesQuery({
        status: GENERAL_STATUS_KO,
        usuallyDisconnected: false,
        customerId: !hasAdminPrivileges ? currentCustomerId : null,
      }),
    [currentCustomerId, hasAdminPrivileges],
  );
  const { data: sourcesData, error: sourcesError, loading: sourcesLoading } = useQuery(queryAlarms);
  const alarms = sourcesData.sources;

  const handleNoAlarms = useCallback(() => {
    setShowAlarms(false);
  }, []);

  const todayEventFilters = useMemo(() => {
    const now = new Date();
    return {
      startDate: startOfDay(now),
      endDate: endOfDay(now),
    };
  }, []);

  return (
    <Dashboard>
      <PageHeaderBar title="app.page.home" />
      <RestrictedToScopedView>
        {customers?.length > 1 ? (
          <QueryResponseHandler error={error} loading={loading} elements={customer}>
            <PageHeaderBar subtitle title="app.page.home.customer" />
            <PageSection>
              <CustomerData customer={customer} />
              <div className={classes.centeredText}>
                <Link color="primary" href="/customer/change">
                  <FormattedMessage id="app.page.home.customer.change" />
                </Link>
              </div>
            </PageSection>
          </QueryResponseHandler>
        ) : null}
      </RestrictedToScopedView>

      <Restricted permissions={[permissions.source_view]}>
        {showAlarms && alarms && alarms.total > 0 ? (
          <QueryResponseHandler error={sourcesError} loading={sourcesLoading} elements={alarms}>
            <PageHeaderBar subtitle title="app.page.home.alarms" />
            <PageSection hideBackground>
              <FormConfirm
                show
                error
                label={intl.formatMessage(
                  { id: "app.page.home.alarms.warning" },
                  { totalAlarms: alarms ? alarms.total : "?" },
                )}
              />
              <FormAdvancedField
                textClassname={classes.alertsCollapsedText}
                label={intl.formatMessage({ id: "common.see" })}
              >
                <AlarmsPaginatedTable
                  pageName="alarmsPage"
                  page={parsePage({ queryParams: search, pageName: "alarmsPage" })}
                  onEmpty={handleNoAlarms}
                  variables={{ customerId: !hasAdminPrivileges ? currentCustomerId : null }}
                />
              </FormAdvancedField>
            </PageSection>
          </QueryResponseHandler>
        ) : null}
      </Restricted>

      <Restricted permissions={[permissions.event_view]}>
        <PageHeaderBar subtitle title="app.page.home.events" buttons={<EventListActionButtons showListButton />} />
        <PageSection hideBackground>
          <EventPaginatedTable
            variables={todayEventFilters}
            noDataMessage={"event.tabs.events.no_today_data"}
            homePage
          />
        </PageSection>
      </Restricted>

      <PageHeaderBar subtitle title="app.page.home.options" />
      <PageSection>
        <MenuContent iconMenu />
      </PageSection>
    </Dashboard>
  );
};

export default withStyles(styles)(HomePage);
